import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import {
  uploadSalesDisplayMedia,
  useDeleteSalesDisplaySlide,
  useReorderSalesDisplaySlides,
  useSalesDisplaySlides,
  useSaveSalesDisplaySlide,
} from "@/hooks/use-sales-display-slides";
import { MIN_SALES_DISPLAY_DURATION_SECONDS } from "@/lib/sales-display-slides";
import { SalesDisplayLoopPreview } from "./admin/SalesDisplayLoopPreview";
import { SalesDisplaySlideForm } from "./admin/SalesDisplaySlideForm";
import { SalesDisplaySlideList } from "./admin/SalesDisplaySlideList";
import { SalesScreenAddress } from "./admin/SalesScreenAddress";
import { EMPTY_FORM, formFromSlide, type FormState } from "./admin/slide-form-state";

/**
 * Slideoppsettet for salgsdisplayet.
 *
 * Orkestrator: skjema, liste og de to previewene bor i `./admin/`. Previewene
 * er VIB-2705 — før dem måtte man åpne en TV for å se hva man hadde laget.
 */
export function SalesDisplayAdmin() {
  const { user } = useAuth();
  const { data: slides = [], isLoading } = useSalesDisplaySlides();
  const saveSlide = useSaveSalesDisplaySlide();
  const deleteSlide = useDeleteSalesDisplaySlide();
  const reorderSlides = useReorderSalesDisplaySlides();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [file, setFile] = useState<File | null>(null);

  const ordered = useMemo(
    () => [...slides].sort((a, b) => a.sortOrder - b.sortOrder),
    [slides],
  );

  useEffect(() => {
    if (!form.id) {
      setForm((current) => ({
        ...current,
        durationSeconds: Math.max(MIN_SALES_DISPLAY_DURATION_SECONDS, current.durationSeconds),
      }));
    }
  }, [form.id]);

  const reset = () => {
    setForm(EMPTY_FORM);
    setFile(null);
  };

  const handleSave = async () => {
    try {
      let mediaPath = form.mediaPath;
      if (file) mediaPath = await uploadSalesDisplayMedia(file);
      if ((form.kind === "image" || form.kind === "video") && !mediaPath) {
        toast.error("Velg en mediefil for denne slide-typen.");
        return;
      }
      await saveSlide.mutateAsync({
        ...form,
        mediaPath,
        widgetKey: form.kind === "widget" ? form.widgetKey : null,
        sortOrder: form.id
          ? ordered.find((slide) => slide.id === form.id)?.sortOrder ?? ordered.length * 10 + 10
          : ordered.length * 10 + 10,
        createdBy: user?.id ?? null,
      });
      toast.success(form.id ? "Sliden er oppdatert." : "Sliden er lagt til.");
      reset();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Kunne ikke lagre sliden.");
    }
  };

  const move = async (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= ordered.length) return;
    const next = [...ordered];
    [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
    try {
      await reorderSlides.mutateAsync(next);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Kunne ikke endre rekkefølgen.");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Salgsdisplay · slideoppsett</CardTitle>
        <p className="text-sm text-muted-foreground">
          Aktive slides går i rekkefølge og starter på nytt etter siste slide. Minimum varighet er 15 sekunder.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <SalesScreenAddress />

        <SalesDisplayLoopPreview />

        <SalesDisplaySlideForm
          form={form}
          onChange={setForm}
          file={file}
          onFileChange={setFile}
          onSave={handleSave}
          onCancel={reset}
          isSaving={saveSlide.isPending}
        />

        <div className="space-y-2">
          <h3 className="font-medium">Rekkefølge</h3>
          <SalesDisplaySlideList
            slides={ordered}
            isLoading={isLoading}
            isReordering={reorderSlides.isPending}
            isDeleting={deleteSlide.isPending}
            onMove={move}
            onEdit={(slide) => {
              setForm(formFromSlide(slide));
              setFile(null);
            }}
            onDelete={async (slide) => {
              try {
                await deleteSlide.mutateAsync(slide);
                toast.success("Sliden er slettet.");
                if (form.id === slide.id) reset();
              } catch (error) {
                toast.error(error instanceof Error ? error.message : "Kunne ikke slette sliden.");
              }
            }}
          />
        </div>
      </CardContent>
    </Card>
  );
}
